import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Send, ArrowRight, CheckCircle } from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', phone: '', company: '', service: '', message: '' });
    }, 5000);
  };

  const contactInfo = [
    { icon: Mail, title: 'Email', details: 'support@Granitecurrent.com', link: 'mailto:support@Granitecurrent.com' },
    { icon: Phone, title: 'Phone', details: '+1 480 439 2436', link: 'tel:+14804392436' },
    { icon: MapPin, title: 'Address', details: '2707 W Desert Ranch Rd, Phoenix, AZ 85086', link: null },
    { icon: Clock, title: 'Hours', details: 'Mon - Fri: 9AM - 6PM MST', link: null }
  ];

  const services = [
    'Print on Demand',
    'Dropshipping Fulfillment',
    'Product Sourcing',
    'Global Warehousing',
    'Custom Branding',
    'TikTok Shop Solutions',
    'Other / Custom Solution'
  ];

  return (
    <div data-testid="contact-page" className="bg-[#fffdf8]">
      {/* Hero */}
      <section className="pt-32 pb-20 warm-gradient">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-[#ea580c] text-sm font-medium tracking-wider uppercase mb-4 block">
              Contact Us
            </span>
            <h1 className="font-['Cormorant_Garamond'] text-5xl md:text-6xl font-semibold text-[#78350f] mb-6" data-testid="contact-hero-title">
              Let's Grow
              <br />
              <span className="text-[#ea580c]">Together</span>
            </h1>
            <p className="text-[#78350f]/80 text-lg">
              Ready to scale your ecommerce business naturally? Get in touch with our warm team.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-['Cormorant_Garamond'] text-3xl font-semibold text-[#78350f] mb-2">
                Get in Touch
              </h2>
              <p className="text-[#78350f]/70 mb-10">
                Granite Current LLC - Your trusted fulfillment partner.
              </p>

              <div className="space-y-6 mb-10">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-start gap-4"
                    data-testid={`contact-info-${index}`}
                  >
                    <div className="w-12 h-12 bg-[#fef3c7] rounded-xl flex items-center justify-center flex-shrink-0">
                      <info.icon size={20} className="text-[#ea580c]" />
                    </div>
                    <div>
                      <h3 className="text-sm text-[#78350f]/60 mb-1">{info.title}</h3>
                      {info.link ? (
                        <a href={info.link} className="text-[#78350f] hover:text-[#ea580c] transition-colors font-medium">
                          {info.details}
                        </a>
                      ) : (
                        <p className="text-[#78350f] font-medium">{info.details}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Decorative Image */}
              <div className="organic-shape-1 overflow-hidden warm-shadow hidden lg:block">
                <img
                  src="https://images.pexels.com/photos/4067525/pexels-photo-4067525.jpeg?auto=compress&cs=tinysrgb&w=600"
                  alt="Our workspace"
                  className="w-full aspect-video object-cover"
                />
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-[#fef3c7]/50 p-8 md:p-10 organic-shape-2"
            >
              {submitted ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-12">
                  <div className="w-20 h-20 bg-[#fef3c7] rounded-full flex items-center justify-center mb-6">
                    <CheckCircle size={40} className="text-[#ea580c]" />
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] text-3xl font-semibold text-[#78350f] mb-3">
                    Message Sent!
                  </h3>
                  <p className="text-[#78350f]/70 mb-6">
                    Thank you for reaching out. We'll get back to you within 24 hours.
                  </p>
                  <Link to="/" className="inline-flex items-center gap-2 text-[#ea580c] font-medium">
                    Back to Home
                    <ArrowRight size={18} />
                  </Link>
                </div>
              ) : (
                <>
                  <h2 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#78350f] mb-2">
                    Send Us a Message
                  </h2>
                  <p className="text-[#78350f]/60 mb-8">
                    Fill out the form and we'll get back to you shortly.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="text-sm font-medium text-[#78350f] mb-2 block">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                          data-testid="input-name"
                          className="w-full bg-white border-2 border-[#fcdcae] px-4 py-3 text-[#78350f] placeholder:text-[#78350f]/40 focus:outline-none focus:border-[#ea580c] transition-colors organic-shape-3"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-[#78350f] mb-2 block">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@example.com"
                          data-testid="input-email"
                          className="w-full bg-white border-2 border-[#fcdcae] px-4 py-3 text-[#78350f] placeholder:text-[#78350f]/40 focus:outline-none focus:border-[#ea580c] transition-colors organic-shape-3"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="text-sm font-medium text-[#78350f] mb-2 block">
                          Phone
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+1 (555) 000-0000"
                          data-testid="input-phone"
                          className="w-full bg-white border-2 border-[#fcdcae] px-4 py-3 text-[#78350f] placeholder:text-[#78350f]/40 focus:outline-none focus:border-[#ea580c] transition-colors organic-shape-3"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-[#78350f] mb-2 block">
                          Company
                        </label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Your Company"
                          data-testid="input-company"
                          className="w-full bg-white border-2 border-[#fcdcae] px-4 py-3 text-[#78350f] placeholder:text-[#78350f]/40 focus:outline-none focus:border-[#ea580c] transition-colors organic-shape-3"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-[#78350f] mb-2 block">
                        Service
                      </label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        data-testid="select-service"
                        className="w-full bg-white border-2 border-[#fcdcae] px-4 py-3 text-[#78350f] focus:outline-none focus:border-[#ea580c] transition-colors organic-shape-3"
                      >
                        <option value="">Select a service</option>
                        {services.map((service) => (
                          <option key={service} value={service}>{service}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-[#78350f] mb-2 block">
                        Message *
                      </label>
                      <textarea
                        name="message"
                        required
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your needs..."
                        data-testid="input-message"
                        className="w-full bg-white border-2 border-[#fcdcae] px-4 py-3 text-[#78350f] placeholder:text-[#78350f]/40 focus:outline-none focus:border-[#ea580c] transition-colors resize-none organic-shape-3"
                      />
                    </div>

                    <button
                      type="submit"
                      data-testid="submit-button"
                      className="w-full bg-[#ea580c] text-white py-4 font-medium hover:bg-[#c2410c] transition-all duration-300 hover:translate-y-[-2px] hover:shadow-lg inline-flex items-center justify-center gap-2 organic-shape-3"
                    >
                      <Send size={18} />
                      Send Message
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Links */}
      <section className="py-16 bg-[#fef3c7]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h3 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#78350f] mb-8 text-center">
            Explore Our Services
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { title: 'Print on Demand', link: '/services#print-on-demand' },
              { title: 'Dropshipping', link: '/services#dropshipping' },
              { title: 'Global Warehousing', link: '/services#global-warehousing' },
              { title: 'Product Sourcing', link: '/services#product-sourcing' },
              { title: 'Custom Branding', link: '/services#custom-branding' },
              { title: 'TikTok Shop', link: '/services#tiktok-shop' }
            ].map((item, index) => (
              <Link
                key={index}
                to={item.link}
                data-testid={`service-link-${index}`}
                className="bg-white px-6 py-4 text-center hover:bg-[#fef3c7] transition-all duration-300 hover:translate-y-[-2px] hover:shadow-lg group border-2 border-[#fcdcae] organic-shape-3"
              >
                <span className="text-[#78350f] font-medium group-hover:text-[#ea580c] transition-colors">
                  {item.title}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
